import { useState, useMemo, useRef, useEffect } from "react";
import { ALL_DAYS, WEEKEND_DAYS, WEEKEND_SPLIT_TIME, TIME_SLOTS, HALF_SLOTS, COURTS, JUNIOR_ELIGIBLE_SLOTS, SCHEDULE_CLOSE_MINUTES, TIER_COLORS, TIER_ORDER } from "../utils/tiers";
import { getOccupiedSlots, getOccupiedSlotsForRange, getGroupSchedule, getEndTime, parseTime } from "../utils/time";
import { getWarnings, getConflictingGroupIds } from "../utils/validation";
import GroupModal from "../components/GroupModal";
import WeekendSessionModal from "../components/WeekendSessionModal";
import { coachShortName } from "../utils/coachColors";
import { coachAvailableFor } from "../utils/coachAvailability";
import { waitlistCount } from "../utils/waitlist";

export default function WeeklyGridView({
  players, groups, coaches = [], movePlayer, updateGroup, removeFromGroup, deleteGroup, createGroup,
  onWarnings, onPlayerClick, onCreateGroup, onNotify,
  onUpdatePlayerTier, onWaitlistAdd, onWaitlistRemove, onWaitlistMove,
}) {
  const [selectedGroup, setSelectedGroup] = useState(null);
  // See GroupsView: selectedGroup only says which group is open, the live
  // record says what's in it.
  const openGroup = selectedGroup ? groups.find(g => g.id === selectedGroup.id) : null;
  const [weekendModal, setWeekendModal] = useState(null); // { mode, session, prefill }
  // Which group cell is mid-drag, and which cell it's hovering over — drives
  // the drop-target highlight while rescheduling a whole group by drag.
  const [draggingGroupId, setDraggingGroupId] = useState(null);
  const [dropTargetKey, setDropTargetKey] = useState(null);
  // Member lookup: type a name to see which session they're in. `pickedId` is
  // set when a name is chosen from the dropdown; with a single match we skip
  // the click and highlight straight away.
  const [search, setSearch] = useState("");
  const [pickedId, setPickedId] = useState(null);
  // The list of matches is only open while you're choosing. Picking someone
  // closes it so the grid underneath — and the session now ringed on it — is
  // visible straight away; typing or refocusing the field opens it again.
  const [matchesOpen, setMatchesOpen] = useState(false);
  const searchBoxRef = useRef(null);
  // Level filter: narrows the week to one tier so a single level can be
  // balanced across the days. Non-matching sessions fade rather than vanish —
  // the courts they occupy still matter when you're looking for a free slot.
  const [tierFilter, setTierFilter] = useState("All");
  const highlightRef = useRef(null);

  const playerMap = Object.fromEntries(players.map(p => [p.id, p]));
  const coachMap = Object.fromEntries(coaches.map(c => [c.id, c]));
  // Group ids that share a court/day/time with another group. When two
  // groups overlap, the occupancy matrix below can only show one per cell,
  // so this badge is how the hidden double-booking still surfaces.
  const conflictIds = getConflictingGroupIds(groups);

  const query = search.trim().toLowerCase();

  // Sessions per level, so the filter can say how much is there before you
  // pick it and an empty level is obvious without selecting it.
  const tierCounts = useMemo(() => {
    const counts = {};
    groups.forEach(g => { counts[g.tier] = (counts[g.tier] || 0) + 1; });
    return counts;
  }, [groups]);

  // Which sessions each matching member sits in. Read off group.playerIds
  // rather than player.groupId so weekend Open Play / Private sessions —
  // which don't set a player's groupId — are found too.
  const matches = useMemo(() => {
    if (!query) return [];
    return players
      .filter(p => p.name.toLowerCase().includes(query))
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, 8)
      .map(p => ({ player: p, sessions: groups.filter(g => (g.playerIds || []).includes(p.id)) }));
  }, [query, players, groups]);

  // A lone match highlights without needing a click; otherwise the coach picks.
  const picked = pickedId
    ? matches.find(m => m.player.id === pickedId) || null
    : matches.length === 1
      ? matches[0]
      : null;
  const highlightIds = new Set(picked ? picked.sessions.map(g => g.id) : []);

  // Clicking anywhere outside the search box closes the list, the same way a
  // native select behaves. Pointerdown rather than click so it closes before
  // a click on the grid underneath is handled.
  useEffect(() => {
    if (!matchesOpen) return;
    const onPointerDown = e => {
      if (!searchBoxRef.current?.contains(e.target)) setMatchesOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [matchesOpen]);

  useEffect(() => {
    if (highlightRef.current) {
      highlightRef.current.scrollIntoView({ block: "center", inline: "center", behavior: "smooth" });
    }
  }, [picked?.player.id, highlightIds.size]);

  // Half-hour occupancy matrix: occupancy[day][court][i] = group occupying
  // that half-hour row, or null. Lets a 1.5-hour session span exactly 3 rows
  // via rowSpan instead of being duplicated across whole-hour cells.
  const occupancy = {};
  ALL_DAYS.forEach(day => {
    occupancy[day] = {};
    COURTS.forEach(court => {
      occupancy[day][court] = HALF_SLOTS.map(() => null);
    });
  });
  groups.forEach(g => {
    if (!g.day || !g.court || !occupancy[g.day]?.[g.court]) return;
    const { startTime, duration } = getGroupSchedule(g);
    if (!startTime || !duration) return;
    const start = parseTime(startTime);
    const end = start + duration * 60;
    HALF_SLOTS.forEach((halfStart, i) => {
      const mins = parseTime(halfStart);
      if (mins >= start && mins < end) occupancy[g.day][g.court][i] = g;
    });
  });

  // Hourly cell map (a group appears once per hour it overlaps) — used only
  // for the CSV export, which is a plain text table and has no notion of
  // spanning cells.
  const cellMap = new Map();
  groups.forEach(g => {
    if (g.day && g.court) {
      getOccupiedSlots(g).forEach(slot => cellMap.set(`${g.day}|${slot}|${g.court}`, g));
    }
  });

  // Scheduling warnings for moving a whole group to a new day/time — mirrors
  // the per-player checks getWarnings runs, but for every member at once and
  // without the composition checks (a move doesn't change who's in the group).
  function groupMoveWarnings(group, day, startTime, duration) {
    const warnings = [];
    if (!startTime || !duration) return warnings;
    if (parseTime(startTime) + duration * 60 > SCHEDULE_CLOSE_MINUTES) {
      warnings.push(`${group.id} would run past closing time`);
    }
    const slots = getOccupiedSlotsForRange(startTime, duration);
    if (group.tier && group.tier.startsWith("Junior") && slots.length > 0 &&
        !slots.every(s => JUNIOR_ELIGIBLE_SLOTS.includes(s))) {
      warnings.push(`${group.id} is a junior group moved outside junior-eligible hours`);
    }
    const unavailable = (group.playerIds || []).filter(id => {
      const p = playerMap[id];
      if (!p || !p.availability) return false;
      return slots.length > 0 && slots.every(slot => !p.availability.includes(`${day}|${slot}`));
    });
    if (unavailable.length > 0) {
      warnings.push(`${unavailable.length} player${unavailable.length !== 1 ? "s" : ""} in ${group.id} not available on ${day} at ${startTime}`);
    }
    const coach = group.coachId ? coachMap[group.coachId] : null;
    if (coach && !coachAvailableFor(coach, day, startTime, duration)) {
      warnings.push(`${coach.name} is not available on ${day} at ${startTime}`);
    }
    return warnings;
  }

  // Move a group onto a grid cell. Empty cell → reschedule to that
  // day/court/time; occupied cell → swap the two groups' slots. Duration is
  // always preserved; each group keeps its own length across a swap.
  function moveGroupToCell(groupId, cell) {
    const source = groups.find(g => g.id === groupId);
    if (!source) return;
    const target = cell.group;
    if (target && target.id === source.id) return; // dropped onto itself

    const s = getGroupSchedule(source);
    if (target) {
      const t = getGroupSchedule(target);
      updateGroup(source.id, { day: target.day, court: target.court, startTime: t.startTime, duration: s.duration });
      updateGroup(target.id, { day: source.day, court: source.court, startTime: s.startTime, duration: t.duration });
      const warnings = [
        ...groupMoveWarnings(source, target.day, t.startTime, s.duration),
        ...groupMoveWarnings(target, source.day, s.startTime, t.duration),
      ];
      if (warnings.length > 0) onWarnings(warnings);
      onNotify({ message: `Swapped ${source.id} and ${target.id}` });
      return;
    }

    updateGroup(source.id, { day: cell.day, court: cell.court, startTime: cell.startTime, duration: s.duration });
    const warnings = groupMoveWarnings(source, cell.day, cell.startTime, s.duration);
    if (warnings.length > 0) onWarnings(warnings);
    onNotify({ message: `Moved ${source.id} to ${cell.day} ${cell.startTime}` });
  }

  function handleDrop(e, cell) {
    e.preventDefault();
    setDraggingGroupId(null);
    setDropTargetKey(null);
    let data;
    try { data = JSON.parse(e.dataTransfer.getData("text/plain")); } catch { return; }

    // Dragging a whole group to reschedule/swap it.
    if (data.type === "group") {
      moveGroupToCell(data.groupId, cell);
      return;
    }

    // Dragging a player into a group (only instruction groups accept players).
    const group = cell.group;
    if (!group || group.kind) return;
    const { playerId, fromGroupId } = data;
    if (fromGroupId === group.id) return;
    const player = playerMap[playerId];
    if (!player) return;
    const warnings = getWarnings(player, group);
    if (warnings.length > 0) onWarnings(warnings);
    movePlayer(playerId, fromGroupId, group.id);
  }

  const modalPlayers = openGroup
    ? openGroup.playerIds.map(id => playerMap[id]).filter(Boolean)
    : [];

  function handleAddToGroup(player) {
    // No optimistic edit of the snapshot: the panel renders from the live
    // group, and an assignment can now stop to ask whether it's a move or a
    // second clinic — showing them in the group before that is answered would
    // be showing something that hasn't happened.
    const target = openGroup || selectedGroup;
    const warnings = getWarnings(player, target);
    if (warnings.length > 0) onWarnings(warnings);
    movePlayer(player.id, player.groupId, target.id);
  }

  function handleRemovePlayer(player) {
    const groupId = selectedGroup.id;
    removeFromGroup(player.id, groupId);
    onNotify({
      message: `Removed ${player.name} from ${groupId}`,
      onUndo: () => movePlayer(player.id, null, groupId),
    });
  }

  function handleExportCSV() {
    const header = ["Time", ...ALL_DAYS.flatMap(d => COURTS.map(c => `${d} – ${c}`))];
    const rows = TIME_SLOTS.map(slot => [
      slot,
      ...ALL_DAYS.flatMap(d =>
        COURTS.map(c => {
          const g = cellMap.get(`${d}|${slot}|${c}`);
          if (!g) return "";
          const names = g.playerIds.map(id => playerMap[id]?.name || id).join(", ");
          return `${g.id}: ${names}`;
        })
      ),
    ]);
    const csv = [header, ...rows]
      .map(row => row.map(v => `"${v.replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "kac_schedule.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  }

  // Tracks the first highlighted cell of this render so the grid can scroll to
  // it; re-initialised every render, before the cells below are built.
  let scrollTargetTaken = false;

  return (
    <div className="p-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-start justify-between mb-3 gap-2 no-print">
        {/* Member lookup */}
        <div className="relative w-full sm:w-80" ref={searchBoxRef}>
          <input
            type="text"
            value={search}
            onChange={e => { setSearch(e.target.value); setPickedId(null); setMatchesOpen(true); }}
            onFocus={() => setMatchesOpen(true)}
            onKeyDown={e => {
              if (e.key !== "Escape") return;
              // First Escape closes the list but keeps the highlight; a second
              // clears the search entirely.
              if (matchesOpen) setMatchesOpen(false);
              else { setSearch(""); setPickedId(null); }
            }}
            placeholder="Search a member — see their session…"
            className="border border-gray-300 rounded px-3 py-1.5 pr-7 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          {search && (
            <button
              onClick={() => { setSearch(""); setPickedId(null); setMatchesOpen(false); }}
              title="Clear search"
              className="absolute right-2 top-1.5 text-gray-400 hover:text-gray-600 leading-none text-lg"
            >
              ×
            </button>
          )}
          {query && matchesOpen && (
            // Above the grid's sticky headers (z-30) but below the app header
            // and nav rail (z-40), which should still win if the page scrolls.
            <div className="absolute z-[35] mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
              {matches.length === 0 && (
                <p className="px-3 py-2 text-sm text-gray-400 italic">No member matches “{search.trim()}”.</p>
              )}
              {matches.map(({ player, sessions }) => {
                const isPicked = picked?.player.id === player.id;
                return (
                  <button
                    key={player.id}
                    onClick={() => { setPickedId(player.id); setMatchesOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-sm border-b border-gray-100 last:border-b-0 transition-colors ${
                      isPicked ? "bg-blue-50" : "hover:bg-gray-50"
                    }`}
                  >
                    <span className="font-medium text-gray-800">{player.name}</span>
                    {sessions.length === 0 ? (
                      <span className="text-gray-400 italic ml-2">not in a session</span>
                    ) : (
                      <span className="text-gray-500 ml-2">{sessions.map(sessionLabel).join("  ·  ")}</span>
                    )}
                    {isPicked && sessions.length > 0 && (
                      <span className="text-blue-600 ml-2 text-xs font-semibold">highlighted →</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
        <select
          value={tierFilter}
          onChange={e => setTierFilter(e.target.value)}
          title="Show only one level — the rest of the week fades back"
          className={`border rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition-colors ${
            tierFilter === "All" ? "border-gray-300 text-gray-600" : "border-blue-400 text-blue-700 font-medium"
          }`}
        >
          <option value="All">All levels</option>
          {TIER_ORDER.filter(t => t !== "No Group").map(t => (
            <option key={t} value={t}>{t} ({tierCounts[t] || 0})</option>
          ))}
        </select>
        <button
          onClick={handleExportCSV}
          className="flex items-center gap-1.5 border border-gray-300 hover:border-gray-400 text-gray-600 hover:text-gray-800 text-sm px-3 py-1.5 rounded transition-colors"
        >
          ↓ Export CSV
        </button>
        <button
          onClick={() => window.print()}
          className="flex items-center gap-1.5 border border-gray-300 hover:border-gray-400 text-gray-600 hover:text-gray-800 text-sm px-3 py-1.5 rounded transition-colors"
        >
          ⎙ Print
        </button>
        </div>
      </div>

      {/* Answer bar — restates where the picked member is, so the answer is
          readable without hunting for the highlighted cell. */}
      {picked && (
        <div className="mb-3 flex flex-wrap items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm no-print">
          <span className="font-semibold text-blue-900">{picked.player.name}</span>
          {picked.sessions.length === 0 ? (
            <span className="text-blue-700">is not in any session yet.</span>
          ) : (
            <span className="text-blue-800">→ {picked.sessions.map(sessionLabel).join("   ·   ")}</span>
          )}
          <button
            onClick={() => onPlayerClick(picked.player)}
            className="ml-auto text-xs font-semibold text-blue-700 hover:text-blue-900 border border-blue-300 rounded px-2 py-1 transition-colors"
          >
            Open card
          </button>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-2 mb-3 no-print">
        {TIER_ORDER.filter(t => t !== "No Group").map(tier => {
          const c = TIER_COLORS[tier];
          return (
            <div key={tier} className={`flex items-center gap-1.5 ${c.bg} ${c.border} border rounded px-2 py-1`}>
              <div className={`w-2.5 h-2.5 rounded-sm ${c.header}`} />
              <span className={`text-xs font-medium ${c.text}`}>{tier}</span>
            </div>
          );
        })}
        {["Open Play", "Private"].map(k => {
          const c = TIER_COLORS[k];
          return (
            <div key={k} className={`flex items-center gap-1.5 ${c.bg} ${c.border} border rounded px-2 py-1`}>
              <div className={`w-2.5 h-2.5 rounded-sm ${c.header}`} />
              <span className={`text-xs font-medium ${c.text}`}>{k} (weekend)</span>
            </div>
          );
        })}
        <div className="flex items-center gap-1.5 bg-green-50 border border-green-200 rounded px-2 py-1">
          <span className="text-green-600 text-xs">★</span>
          <span className="text-xs font-medium text-green-700">Junior-eligible slot</span>
        </div>
        <div className="flex items-center gap-1.5 bg-red-50 border border-red-200 rounded px-2 py-1">
          <span className="text-red-600 text-xs">⚠</span>
          <span className="text-xs font-medium text-red-700">Double-booked court/time</span>
        </div>
      </div>

      <p className="text-xs text-gray-400 mb-1.5 sm:hidden no-print">← Scroll to see the full week →</p>

      {/* Scrollable grid */}
      {/* Clamp measured off the real header height rather than a fixed 160px,
          which drifted when the header lost its tab row. */}
      <div className="overflow-auto border border-gray-200 rounded-lg" style={{ maxHeight: "calc(100vh - var(--header-h, 60px) - 7rem)" }}>
        <table className="border-collapse text-xs w-full min-w-max">
          <thead>
            <tr>
              <th className="border border-gray-300 bg-gray-100 px-2 py-1.5 text-left font-semibold text-gray-600 w-28 sticky top-0 left-0 z-30">
                Time
              </th>
              {ALL_DAYS.map(day => (
                <th
                  key={day}
                  colSpan={4}
                  className="border border-gray-300 bg-gray-50 px-2 py-1.5 text-center font-semibold text-gray-700 sticky top-0 z-20"
                >
                  {day}
                </th>
              ))}
            </tr>
            <tr>
              <th className="border border-gray-300 bg-gray-100 sticky top-9 left-0 z-30" />
              {ALL_DAYS.map(day =>
                COURTS.map(court => {
                  const isTennis = court.startsWith("Tennis");
                  return (
                    <th
                      key={`${day}-${court}`}
                      className={`border border-gray-300 px-1 py-1 text-center font-medium text-gray-600 whitespace-nowrap sticky top-9 z-20 ${isTennis ? "bg-blue-100" : "bg-amber-100"}`}
                      style={{ minWidth: "80px" }}
                    >
                      {court.replace(" Court ", " C")}
                    </th>
                  );
                })
              )}
            </tr>
          </thead>
          <tbody>
            {HALF_SLOTS.map((halfStart, i) => {
              const isHourStart = i % 2 === 0;
              const hourSlot = TIME_SLOTS[Math.floor(i / 2)];
              const isJuniorEligible = JUNIOR_ELIGIBLE_SLOTS.includes(hourSlot);
              return (
                <tr key={halfStart} className={isJuniorEligible ? "bg-green-50" : ""}>
                  {isHourStart && (
                    <td
                      rowSpan={2}
                      className={`border border-gray-300 px-2 py-1.5 font-medium text-gray-600 whitespace-nowrap sticky left-0 z-10 ${isJuniorEligible ? "bg-green-100" : "bg-gray-50"}`}
                    >
                      {isJuniorEligible && <span className="mr-1 text-green-600">&#9733;</span>}
                      {hourSlot}
                    </td>
                  )}
                  {ALL_DAYS.map(day =>
                    COURTS.map(court => {
                      const col = occupancy[day][court];
                      const group = col[i];
                      const continuesPrevious = group && i > 0 && col[i - 1] === group;
                      if (continuesPrevious) return null;

                      let span = 1;
                      if (group) {
                        while (i + span < HALF_SLOTS.length && col[i + span] === group) span++;
                      }

                      const isTennis = court.startsWith("Tennis");
                      const isWeekend = WEEKEND_DAYS.includes(day);
                      const isSplit = isWeekend && halfStart === WEEKEND_SPLIT_TIME;
                      const colors = group ? (TIER_COLORS[group.tier] || TIER_COLORS["No Group"]) : null;
                      const isConflict = group && conflictIds.has(group.id);
                      const member0 = group ? playerMap[group.playerIds[0]] : null;
                      const cellKey = `${day}-${halfStart}-${court}`;
                      const cell = { group, day, court, startTime: halfStart };
                      // A cell accepts a drop when a group is being dragged
                      // (any cell is a valid move/swap target) or, for player
                      // drags, only when it holds an instruction group.
                      const acceptsDrop = draggingGroupId || (group && !group.kind);
                      const isDragSource = group && draggingGroupId === group.id;
                      const isDropTarget = draggingGroupId && dropTargetKey === cellKey && !isDragSource;
                      // Search highlight: the picked member's session is ringed
                      // and everything else fades back.
                      const isHit = group && highlightIds.has(group.id);
                      // Faded by either the member search or the level filter.
                      const offLevel = tierFilter !== "All" && group && group.tier !== tierFilter;
                      const isFaded = (highlightIds.size > 0 && !isHit) || offLevel;
                      let cellRef;
                      if (isHit && !scrollTargetTaken) {
                        scrollTargetTaken = true;
                        cellRef = highlightRef;
                      }
                      return (
                        <td
                          key={cellKey}
                          ref={cellRef}
                          rowSpan={span}
                          draggable={!!group}
                          onDragStart={e => {
                            if (!group) return;
                            e.dataTransfer.effectAllowed = "move";
                            e.dataTransfer.setData("text/plain", JSON.stringify({ type: "group", groupId: group.id }));
                            setDraggingGroupId(group.id);
                          }}
                          onDragEnd={() => { setDraggingGroupId(null); setDropTargetKey(null); }}
                          onDrop={e => handleDrop(e, cell)}
                          onDragOver={e => {
                            if (!acceptsDrop) return;
                            e.preventDefault();
                            if (draggingGroupId && dropTargetKey !== cellKey) setDropTargetKey(cellKey);
                          }}
                          onDragLeave={() => { if (dropTargetKey === cellKey) setDropTargetKey(null); }}
                          onClick={() => {
                            if (group && group.kind) {
                              setWeekendModal({ mode: "edit", session: group });
                            } else if (group) {
                              setSelectedGroup(group);
                            } else if (isWeekend) {
                              setWeekendModal({ mode: "create", prefill: { day, startTime: halfStart, court } });
                            } else {
                              onCreateGroup({ day, startTime: halfStart, court });
                            }
                          }}
                          title={group ? "Drag to reschedule this session" : undefined}
                          className={`
                            px-1 py-1 text-center ${group ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"}
                            ${isConflict ? "border-2 border-red-500" : "border border-gray-200"}
                            ${isSplit ? "border-t-2 border-t-indigo-400" : ""}
                            ${isDropTarget ? "outline outline-2 outline-blue-500 outline-offset-[-2px]" : ""}
                            ${isHit ? "outline outline-[3px] outline-blue-600 outline-offset-[-3px] relative z-10" : ""}
                            ${isFaded ? "opacity-25" : ""}
                            ${isDragSource ? "opacity-40" : ""}
                            ${group
                              ? `${colors.bg} hover:brightness-95`
                              : isWeekend
                                ? "bg-indigo-50/40 hover:bg-indigo-50"
                                : isTennis
                                  ? "bg-blue-50 hover:bg-blue-100"
                                  : "bg-amber-50 hover:bg-amber-100"
                            }
                          `}
                        >
                          {group ? (
                            group.kind ? (
                              <div className="text-left leading-tight">
                                <div className={`font-semibold ${colors.text} flex items-center gap-1`}>
                                  {group.coachId && coachMap[group.coachId] && (
                                    <span className="text-[10px] font-semibold px-1 py-0.5 rounded flex-shrink-0 text-white" style={{ backgroundColor: coachMap[group.coachId].color || "#64748b" }} title={`Coach: ${coachMap[group.coachId].name}`}>
                                      {coachShortName(coachMap[group.coachId].name)}
                                    </span>
                                  )}
                                  {group.kind === "private" ? "Private" : "Open Play"}
                                  {isConflict && <span className="text-red-600" title="Double-booked: another session shares this court and time">⚠</span>}
                                </div>
                                <div className="text-gray-500 truncate">
                                  {group.kind === "private"
                                    ? (member0?.name || "—")
                                    : `${group.playerIds.length} member${group.playerIds.length !== 1 ? "s" : ""}`}
                                </div>
                              </div>
                            ) : (
                              <div>
                                <div className={`font-mono font-bold ${colors.text} flex items-center gap-1`}>
                                  {group.coachId && coachMap[group.coachId] && (
                                    <span
                                      className="text-[10px] font-semibold px-1 py-0.5 rounded flex-shrink-0 text-white"
                                      style={{ backgroundColor: coachMap[group.coachId].color || "#64748b" }}
                                      title={`Coach: ${coachMap[group.coachId].name}`}
                                    >
                                      {coachShortName(coachMap[group.coachId].name)}
                                    </span>
                                  )}
                                  {group.id}
                                  {group.notes && <span className="ml-0.5" title="This group has notes">📝</span>}
                                  {isConflict && <span className="ml-0.5 text-red-600" title="Double-booked: another group shares this court and time">⚠</span>}
                                </div>
                                <div className={`${group.playerIds.length >= 4 ? "text-green-700 font-semibold" : "text-gray-500"}`}>
                                  {group.playerIds.length}/4
                                  {waitlistCount(group) > 0 && (
                                    <span className="ml-1 text-slate-600 font-semibold" title={`${waitlistCount(group)} waiting to join`}>
                                      ⏳{waitlistCount(group)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )
                          ) : (
                            <span className="text-gray-200 text-base leading-none">+</span>
                          )}
                        </td>
                      );
                    })
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selectedGroup && openGroup && (
        <GroupModal
          key={selectedGroup.id}
          group={openGroup}
          groupPlayers={modalPlayers}
          allPlayers={players}
          allGroups={groups}
          coaches={coaches}
          onClose={() => setSelectedGroup(null)}
          onPlayerClick={onPlayerClick}
          onAddPlayer={handleAddToGroup}
          onRemovePlayer={handleRemovePlayer}
          onUpdatePlayerTier={onUpdatePlayerTier}
          onWaitlistAdd={onWaitlistAdd}
          onWaitlistRemove={onWaitlistRemove}
          onWaitlistMove={onWaitlistMove}
          onDeleteGroup={groupId => { deleteGroup(groupId); setSelectedGroup(null); }}
          onUpdateGroup={(groupId, updates) => {
            updateGroup(groupId, updates);
            setSelectedGroup(prev => (prev && prev.id === groupId ? { ...prev, ...updates } : prev));
          }}
        />
      )}

      {weekendModal && (
        <WeekendSessionModal
          mode={weekendModal.mode}
          session={weekendModal.session}
          prefill={weekendModal.prefill}
          players={players}
          coaches={coaches}
          onCreate={data => createGroup(data)}
          onUpdate={(id, data) => updateGroup(id, data)}
          onDelete={id => deleteGroup(id)}
          onClose={() => setWeekendModal(null)}
        />
      )}
    </div>
  );
}

// One-line "where is this session" label for the member lookup — the group id
// for instruction groups, the kind for weekend sessions (which have no id a
// coach would recognise).
function sessionLabel(g) {
  const { startTime } = getGroupSchedule(g);
  const name = g.kind ? (g.kind === "private" ? "Private" : "Open Play") : g.id;
  const when = g.day && startTime ? `${g.day} ${startTime}–${getEndTime(g)}` : "unscheduled";
  return `${name} · ${when}${g.court ? ` · ${g.court}` : ""}`;
}
